import { useEffect, useState, useCallback } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { EnvelopeOpen, PaperPlaneTilt, CalendarPlus, MapPin, Tag, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "./DateInput";
const STATUS_LABEL = {
  invited: { label: "Awaiting your quote", tone: "bg-amber-100 text-amber-800" },
  submitted: { label: "Quote submitted", tone: "bg-blue-100 text-blue-800" },
  accepted_awaiting_start_date: { label: "Accepted — needs start date", tone: "bg-emerald-100 text-emerald-800" },
  start_date_submitted: { label: "Start date sent", tone: "bg-indigo-100 text-indigo-800" },
  admin_countered: { label: "Fixlr counter-proposed a date", tone: "bg-purple-100 text-purple-800" },
  start_date_agreed: { label: "Start date agreed", tone: "bg-emerald-100 text-emerald-800" },
  converted_to_job: { label: "Converted to job", tone: "bg-emerald-100 text-emerald-800" },
  declined: { label: "Declined by client", tone: "bg-slate-100 text-slate-700" },
};

export default function SupplierQuoteInvitations() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quote-submission dialog state
  const [quoting, setQuoting] = useState(null); // jr_id
  const [quoteForm, setQuoteForm] = useState({ amount: "", description: "", validity_date: "" });
  const [submitting, setSubmitting] = useState(false);

  // Start-date dialog state
  const [startingFor, setStartingFor] = useState(null); // jr_id
  const [startForm, setStartForm] = useState({ proposed_start_date: "", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/supplier-portal/quote-invitations");
      setInvites(data.invitations || []);
    } catch {
      toast.error("Couldn't load your invitations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openQuote = (inv) => {
    setQuoteForm({
      amount: inv.my_quote?.amount || "",
      description: inv.my_quote?.description || "",
      validity_date: inv.my_quote?.validity_date || "",
    });
    setQuoting(inv);
  };

  const submitQuote = async () => {
    const amount = parseFloat(quoteForm.amount);
    if (!amount || amount <= 0) { toast.error("Amount is required"); return; }
    setSubmitting(true);
    try {
      await API.post(`/supplier-portal/quote-invitations/${quoting.jr_id}/submit`, {
        amount,
        description: quoteForm.description,
        validity_date: quoteForm.validity_date,
      });
      toast.success("Quote submitted to Fixlr");
      setQuoting(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStartDate = async () => {
    if (!startForm.proposed_start_date) { toast.error("Pick a date"); return; }
    setSubmitting(true);
    try {
      await API.post(`/supplier-portal/quote-invitations/${startingFor.jr_id}/start-date`, startForm);
      toast.success("Start date sent — admin will confirm.");
      setStartingFor(null);
      setStartForm({ proposed_start_date: "", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send start date");
    } finally {
      setSubmitting(false);
    }
  };

  const acceptCounter = async (inv) => {
    if (!window.confirm(`Accept Fixlr's proposed start date of ${inv.my_quote?.admin_counter_start_date}?`)) return;
    try {
      await API.post(`/supplier-portal/quote-invitations/${inv.jr_id}/accept-counter`);
      toast.success("Counter accepted — start date confirmed.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept counter");
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading invitations…</p>;
  if (invites.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg" data-testid="no-invitations-empty">
        No quote invitations yet. When Fixlr asks you to quote on a job, it'll appear here.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="quote-invitations-list">
      {invites.map(inv => {
        const meta = STATUS_LABEL[inv.ui_status] || { label: inv.ui_status, tone: "bg-muted text-foreground" };
        const isAwaitingQuote = inv.ui_status === "invited";
        const canResubmit = inv.ui_status === "submitted";
        const canStartDate = inv.ui_status === "accepted_awaiting_start_date";
        const hasCounter = inv.ui_status === "admin_countered";
        return (
          <div key={inv.jr_id} className="p-3 rounded-lg border border-border bg-card" data-testid={`invitation-${inv.jr_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <EnvelopeOpen size={14} className="text-blue-500 shrink-0" />
                  <h3 className="font-semibold truncate">{inv.title}</h3>
                  <Badge className={`text-[9px] uppercase ${meta.tone}`}>{meta.label}</Badge>
                  {inv.priority && <Badge variant="outline" className="text-[9px] uppercase">{inv.priority}</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{inv.reference}{inv.category ? ` · ${inv.category.replace(/_/g, " ")}` : ""}</p>
                {inv.property_address && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1"><MapPin size={11} /> {inv.property_address}</p>
                )}
                {inv.requested_completion_date && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1"><Clock size={11} /> Wanted by {fmtDate(inv.requested_completion_date)}</p>
                )}
                {inv.my_quote && (
                  <p className="text-[11px] mt-1">Your quote: <strong>£{Number(inv.my_quote.amount || 0).toLocaleString()}</strong>
                    {inv.my_quote.proposed_start_date && <> · Start: <strong>{inv.my_quote.proposed_start_date}</strong></>}
                  </p>
                )}
                {hasCounter && (
                  <div className="mt-2 p-2 rounded-md bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800" data-testid={`counter-banner-${inv.jr_id}`}>
                    <p className="text-[11px] font-semibold text-purple-900 dark:text-purple-100">Fixlr suggests <strong>{inv.my_quote.admin_counter_start_date}</strong> instead</p>
                    {inv.my_quote.admin_counter_note && (
                      <p className="text-[11px] text-purple-800 dark:text-purple-200 mt-0.5">"{inv.my_quote.admin_counter_note}"</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isAwaitingQuote && (
                  <Button size="sm" onClick={() => openQuote(inv)} data-testid={`submit-quote-btn-${inv.jr_id}`}>
                    <PaperPlaneTilt size={12} className="mr-1.5" /> Submit quote
                  </Button>
                )}
                {canResubmit && (
                  <Button size="sm" variant="outline" onClick={() => openQuote(inv)} data-testid={`resubmit-quote-btn-${inv.jr_id}`}>
                    Update quote
                  </Button>
                )}
                {canStartDate && (
                  <Button size="sm" onClick={() => setStartingFor(inv)} data-testid={`propose-start-date-btn-${inv.jr_id}`}>
                    <CalendarPlus size={12} className="mr-1.5" /> Propose start date
                  </Button>
                )}
                {hasCounter && (
                  <>
                    <Button size="sm" onClick={() => acceptCounter(inv)} data-testid={`accept-counter-btn-${inv.jr_id}`}>
                      <CheckCircle size={12} className="mr-1.5" /> Accept their date
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStartingFor(inv)} data-testid={`counter-back-btn-${inv.jr_id}`}>
                      Counter back
                    </Button>
                  </>
                )}
                {inv.ui_status === "start_date_submitted" && (
                  <Badge variant="secondary" className="text-[10px]"><CheckCircle size={11} className="mr-1" /> Awaiting confirmation</Badge>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Submit quote dialog */}
      <Dialog open={!!quoting} onOpenChange={(o) => { if (!o) setQuoting(null); }}>
        <DialogContent data-testid="invitation-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Submit your quote</DialogTitle>
            <DialogDescription>
              {quoting?.title} · {quoting?.reference}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (£) *</Label>
              <Input type="number" min="0" step="0.01" value={quoteForm.amount}
                onChange={e => setQuoteForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="invitation-quote-amount" />
            </div>
            <div>
              <Label>Description / scope</Label>
              <Textarea rows={3} value={quoteForm.description}
                onChange={e => setQuoteForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What's included, materials, timing notes..." />
            </div>
            <div>
              <Label>Quote validity date</Label>
              <DateInput value={quoteForm.validity_date}
                onChange={e => setQuoteForm(f => ({ ...f, validity_date: e.target.value }))} />
            </div>
            <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <Tag size={11} /> Your quote goes straight into the job request — no markups added by you.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuoting(null)}>Cancel</Button>
            <Button onClick={submitQuote} disabled={submitting} data-testid="confirm-submit-quote">
              {submitting ? "Submitting…" : "Submit to Fixlr"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Propose start date dialog */}
      <Dialog open={!!startingFor} onOpenChange={(o) => { if (!o) setStartingFor(null); }}>
        <DialogContent data-testid="invitation-start-date-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Propose a start date</DialogTitle>
            <DialogDescription>
              The client approved your quote — please suggest when you can start.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Proposed start date *</Label>
              <DateInput value={startForm.proposed_start_date}
                onChange={e => setStartForm(f => ({ ...f, proposed_start_date: e.target.value }))}
                data-testid="proposed-start-date-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={startForm.note}
                onChange={e => setStartForm(f => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Can start 9am, will need access to the cellar" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStartingFor(null)}>Cancel</Button>
            <Button onClick={submitStartDate} disabled={submitting} data-testid="confirm-start-date">
              {submitting ? "Sending…" : "Send to Fixlr"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
